import {
  getTopProductsEndpointIdentifier,
  useGetDealCategoryTreeApi,
  useGetTopProductsApi,
} from 'apis/supplier-prices';
import { findCategoryPath } from 'components/category-picker/category-tree-helpers';
import { Box, Button, Flex, HStack, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr } from 'design-system';
import { usePageParams } from 'hooks/use-page-params';
import { VariantPriceEditor } from 'pages/prices/components/variant-price-editor';
import { formatPrice } from 'pages/prices/utils/matrix-display';
import { uomLadder } from 'pages/prices/utils/uom-display';
import { orderWorklist, worklistStaleness } from 'pages/prices/utils/worklist-display';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { FiArrowUpRight, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { useQueryClient } from 'react-query';

export interface TopProductsPanelProps {
  onOpenFamily: (familyId: number) => void;
}

// The Daily Update List: membership is manually flagged variants (the star
// toggle in Master Products), display order is staleness bucket (overdue,
// aging, fresh) with rank inside each bucket — the queue the pricing team
// works top-to-bottom each morning. Clicking a row expands an inline
// per-supplier price editor for that variant; the corner link opens the
// family in the matrix tab.
export const TopProductsPanel = ({ onOpenFamily }: TopProductsPanelProps) => {
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();

  // The productId param doubles as "which row is expanded" while view=top, so
  // tab switches keep the same variant in focus on both surfaces.
  const expandedProductId = Number(params.productId) || undefined;

  const toggleExpand = (masterProductId: number) => {
    setPageParams({
      ...getPageParams(),
      productId: expandedProductId === masterProductId ? undefined : masterProductId,
    });
  };

  // Worklist rows refresh when an expansion is LEFT (collapse or switch), not
  // per cell commit — an immediate refetch would re-sort the queue and yank
  // the expanded panel mid-edit. Leaving by unmount is covered by React
  // Query's refetch-on-mount when the panel returns.
  const queryClient = useQueryClient();
  const prevExpandedRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (prevExpandedRef.current != null && prevExpandedRef.current !== expandedProductId) {
      queryClient.invalidateQueries([getTopProductsEndpointIdentifier]);
    }
    prevExpandedRef.current = expandedProductId;
  }, [expandedProductId, queryClient]);

  const { data, isLoading, isError } = useGetTopProductsApi();
  const rows = useMemo(() => orderWorklist(data?.data ?? []), [data]);

  const { data: categoryTreeData } = useGetDealCategoryTreeApi();
  const categoryTree = categoryTreeData?.data ?? [];

  const categoryLabel = (catalogCategoryId: number) => {
    const path = findCategoryPath(categoryTree, catalogCategoryId);
    return path.length ? path.map((n) => n.title || `Untitled (${n.id})`).join(' › ') : `#${catalogCategoryId}`;
  };

  return (
    <Flex direction='column' h='full' minH={0}>
      <Flex align='center' justify='space-between' wrap='wrap' rowGap='6px' p='16px 20px 12px'>
        <Box>
          <Text fontSize='15px' fontWeight='semibold' color='gray.800'>
            Daily Update List
          </Text>
          <Text fontSize='12px' color='gray.500' mt='2px'>
            Manually flagged variants, most overdue prices first
          </Text>
        </Box>

        <Text fontSize='10.5px' fontWeight='semibold' letterSpacing='0.09em' color='gray.500' textTransform='uppercase'>
          {rows.length} top products
        </Text>
      </Flex>

      {isLoading ? (
        <Flex justify='center' align='center' flex='1'>
          <Spinner size='sm' color='red.500' />
        </Flex>
      ) : isError ? (
        <Flex justify='center' align='center' flex='1'>
          <Text fontSize='13px' color='gray.500'>
            Couldn&apos;t load the update list. Try again shortly.
          </Text>
        </Flex>
      ) : rows.length === 0 ? (
        <Flex justify='center' align='center' flex='1'>
          <Text fontSize='13px' color='gray.500'>
            No top products flagged yet — mark variants with ★ in Master Products.
          </Text>
        </Flex>
      ) : (
        <Box flex='1' overflowY='auto' overflowX='auto' minH={0}>
          <Table size='sm'>
            <Thead position='sticky' top={0} bg='white' zIndex={1}>
              <Tr>
                <Th w='56px'>#</Th>
                <Th>Variant</Th>
                <Th isNumeric>Inquiries (30d)</Th>
                <Th isNumeric>Suppliers</Th>
                <Th isNumeric>Best price</Th>
                <Th>Last price update</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => {
                const staleness = worklistStaleness(row.lastPriceUpdateAt);
                const isExpanded = row.masterProductId === expandedProductId;

                return (
                  <Fragment key={row.masterProductId}>
                    <Tr
                      cursor='pointer'
                      bg={isExpanded ? 'red.50' : undefined}
                      _hover={{ bg: isExpanded ? 'red.50' : 'gray.50' }}
                      onClick={() => toggleExpand(row.masterProductId)}>
                      <Td color='gray.500' fontSize='12.5px'>
                        <HStack spacing='4px'>
                          {isExpanded ? <FiChevronDown size='13px' /> : <FiChevronRight size='13px' />}
                          <Text as='span'>{row.rank}</Text>
                        </HStack>
                      </Td>
                      <Td>
                        <Text fontSize='13.5px' fontWeight='medium' color='gray.800' noOfLines={1}>
                          {row.universalName}
                        </Text>
                        <Text fontSize='11.5px' color='gray.600' noOfLines={1}>
                          {[[row.brand, row.spec].filter(Boolean).join(' '), uomLadder(row)]
                            .filter(Boolean)
                            .join(' · ')}
                        </Text>
                        <Text fontSize='11px' color='gray.500' noOfLines={1}>
                          {categoryLabel(row.catalogCategoryId)}
                        </Text>
                      </Td>
                      <Td isNumeric fontSize='13px'>
                        {row.inquiryCount}
                      </Td>
                      <Td isNumeric fontSize='13px'>
                        {row.supplierCount}
                      </Td>
                      <Td isNumeric fontSize='13px'>
                        {row.bestPrice == null
                          ? '—'
                          : `AED ${formatPrice(row.bestPrice)}${row.bestPriceUnit ? `/${row.bestPriceUnit}` : ''}`}
                      </Td>
                      <Td>
                        <HStack spacing='6px'>
                          <Box w='7px' h='7px' borderRadius='full' bg={staleness.color} flexShrink={0} />
                          <Text fontSize='12.5px' color='gray.700'>
                            {staleness.label}
                          </Text>
                        </HStack>
                      </Td>
                    </Tr>
                    {isExpanded && (
                      <Tr>
                        <Td colSpan={6} p={0}>
                          <Box borderTop='1px solid' borderColor='red.100'>
                            <Flex justify='flex-end' px='12px' py='4px' bg='#FAFAFB'>
                              <Button
                                size='xs'
                                variant='ghost'
                                color='gray.600'
                                fontWeight='medium'
                                rightIcon={<FiArrowUpRight size='13px' />}
                                onClick={() => onOpenFamily(row.familyId)}>
                                Open in Price Matrix
                              </Button>
                            </Flex>
                            <Box borderTop='1px solid' borderColor='gray.200' bg='white'>
                              <VariantPriceEditor masterProductId={row.masterProductId} familyId={row.familyId} />
                            </Box>
                          </Box>
                        </Td>
                      </Tr>
                    )}
                  </Fragment>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </Flex>
  );
};
